import { useState, type FormEvent, type ReactNode } from 'react';

import { asApiError, type ApiError } from '@/api/errors';
import { useApi } from '@/api/useApi';
import { ROLE_LABELS } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { describeApiError } from '@/i18n/errorMessages';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Card, CardBody, CardHeader } from '@/ui/Card';

/**
 * Mi cuenta: quién soy y cambiar mi contraseña (#192).
 *
 * Existe porque la tabla de Usuarios esconde a propósito las acciones de la
 * fila propia —nadie se cambia el rol ni se da de baja a sí mismo— y eso
 * arrastraba al reseteo de contraseña. El primer administrador de un ambiente
 * quedaba sin forma de dejar de usar la clave del arranque en frío.
 *
 * Lo que esta vista decide, y por qué
 * ----------------------------------
 * **Pide la contraseña actual** aunque la sesión esté abierta. Un token
 * robado de una pestaña no tiene que alcanzar para dejar afuera a la dueña de
 * la cuenta cambiándole la clave.
 *
 * **Después del cambio vuelve a entrar sola.** La API cierra todas las
 * sesiones, incluida ésta —si el motivo es que la clave se filtró, la sesión
 * que la usó no puede sobrevivir—. Como acá están el correo y la clave nueva,
 * se inicia sesión de nuevo en el acto y la persona no nota el corte; las
 * demás sesiones sí quedan cerradas, que es el efecto que se busca.
 *
 * **La confirmación se valida acá y no en la API.** Es un error de tipeo, no
 * una regla de negocio: se avisa antes de mandar nada.
 */
export function AccountPage(): ReactNode {
  const { t } = useI18n();
  const { claims } = useAuth();

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <h1 className="text-lg font-semibold text-ink">{t('account.title')}</h1>

      {claims !== null && (
        <Card>
          <CardHeader title={t('account.identity.title')} />
          <CardBody>
            <dl className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
              <Item label={t('account.identity.name')}>{claims.name ?? '—'}</Item>
              <Item label={t('account.identity.email')}>{claims.email}</Item>
              <Item label={t('auth.role.label')}>
                <Badge tone="neutral">{ROLE_LABELS[claims.role]}</Badge>
              </Item>
            </dl>
          </CardBody>
        </Card>
      )}

      <PasswordCard />
    </div>
  );
}

function Item({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 truncate text-ink">{children}</dd>
    </div>
  );
}

interface PasswordChanged {
  sessions_revoked: number;
  audit_seq: number;
}

/** Mismo mínimo que `MIN_PASSWORD_CHARS` en `schemas/users.py`. La API lo
 *  vuelve a comprobar; acá sólo evita un viaje por un error obvio. */
const MIN_PASSWORD_CHARS = 12;

type Notice = { kind: 'ok'; othersClosed: number } | { kind: 'relogin' } | null;

function PasswordCard(): ReactNode {
  const { t } = useI18n();
  const api = useApi();
  const { claims, login } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    setLocalError(null);
    setError(null);
    setNotice(null);
    if (current === '' || next === '' || confirm === '') {
      setLocalError(t('account.password.required'));
      return;
    }
    if (next.length < MIN_PASSWORD_CHARS) {
      setLocalError(t('account.password.tooShort'));
      return;
    }
    if (next !== confirm) {
      setLocalError(t('account.password.mismatch'));
      return;
    }
    if (next === current) {
      setLocalError(t('account.password.same'));
      return;
    }
    if (claims === null) return;

    setSubmitting(true);
    const { tenant, email } = claims;
    api
      .post<PasswordChanged>('/api/v1/auth/password', {
        current_password: current,
        new_password: next,
      })
      .then(async (result) => {
        // La API acaba de cerrar esta sesión también. Se vuelve a entrar con
        // la clave nueva antes de que cualquier otra consulta reciba un 401.
        try {
          await login({ tenant, email, password: next });
          setNotice({ kind: 'ok', othersClosed: Math.max(0, result.sessions_revoked - 1) });
        } catch {
          // El cambio ya está hecho; sólo falló volver a entrar. La guarda de
          // sesión lleva al login y la clave nueva sirve.
          setNotice({ kind: 'relogin' });
        }
        setCurrent('');
        setNext('');
        setConfirm('');
      })
      .catch((cause: unknown) => {
        setError(asApiError(cause));
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const described = error === null ? null : describeApiError(error, t);

  return (
    <Card>
      <CardHeader
        title={t('account.password.title')}
        description={t('account.password.intro')}
      />
      <CardBody>
        <form onSubmit={handleSubmit} className="max-w-sm space-y-4" noValidate>
          <Field
            id="current-password"
            label={t('account.password.current')}
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
          />
          <Field
            id="new-password"
            label={t('account.password.new')}
            hint={t('account.password.newHint')}
            value={next}
            onChange={setNext}
            autoComplete="new-password"
          />
          <Field
            id="confirm-password"
            label={t('account.password.confirm')}
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
          />

          {localError !== null && (
            <p
              role="alert"
              className="rounded border border-critical/30 bg-critical-soft p-3 text-sm text-ink"
            >
              {localError}
            </p>
          )}

          {described !== null && (
            <p
              role="alert"
              className="rounded border border-critical/30 bg-critical-soft p-3 text-sm text-ink"
            >
              {described.title}
              {described.traceId !== null && (
                <span className="mt-1 block text-xs text-ink-subtle">
                  {t('state.error.trace')}:{' '}
                  <code className="font-mono select-all">{described.traceId}</code>
                </span>
              )}
            </p>
          )}

          {notice !== null && (
            <p
              role="status"
              className="rounded border border-verified/30 bg-verified-soft p-3 text-sm text-ink"
            >
              {notice.kind === 'ok'
                ? notice.othersClosed > 0
                  ? t('account.password.changedOthers', { n: String(notice.othersClosed) })
                  : t('account.password.changed')
                : t('account.password.changedRelogin')}
            </p>
          )}

          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? t('account.password.submitting') : t('account.password.submit')}
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function Field({
  id,
  label,
  hint,
  value,
  onChange,
  autoComplete,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
}): ReactNode {
  const hintId = `${id}-hint`;
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-ink">
        {label}
      </label>
      <input
        id={id}
        name={id}
        type="password"
        value={value}
        required
        autoComplete={autoComplete}
        {...(hint === undefined ? {} : { 'aria-describedby': hintId })}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink"
      />
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
    </div>
  );
}
